import { useEffect, useRef, useState } from 'react';
import './App.css';

const GRAPHQL_ENDPOINT = process.env.REACT_APP_GRAPHQL_ENDPOINT || '/graphql';
const API_BASE_URL = process.env.REACT_APP_API_URL;
// Default mutation can be overridden via REACT_APP_CHAT_MUTATION if the schema differs.

const initialMessages = [
  {
    id: 'assistant-greeting',
    role: 'assistant',
    content: '你好！我是你的 AI 助手，随时可以回答和解释你的问题。',
  },
];

function App() {
  const [messages, setMessages] = useState(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const scrollAnchorRef = useRef(null);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    const optimisticMessages = [...messages, userMessage];
    setMessages(optimisticMessages);
    setInputValue('');
    setIsSending(true);
    setError('');

    try {
      const assistantMessage = await fetchAssistantReply(
        optimisticMessages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        }))
      );

      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: assistantMessage,
        },
      ]);
    } catch (err) {
      setError(err.message || '请求失败，请稍后重试。');
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    void sendMessage();
  };

  const handleTextareaKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  return (
    <div className="app-shell">
      <header className="chat-header">
        <div>
          <h1>GraphQL AI Chat</h1>
          <p>与大模型实时对话，消息通过 GraphQL 传输。</p>
        </div>
        <span className="endpoint-chip">{GRAPHQL_ENDPOINT}</span>
      </header>

      <section className="chat-window">
        {messages.map((message) => (
          <article
            key={message.id}
            className={`chat-bubble ${message.role === 'user' ? 'user' : 'assistant'}`}
          >
            <strong>{message.role === 'user' ? '你' : 'AI'}</strong>
            <p>{message.content}</p>
          </article>
        ))}
        <div ref={scrollAnchorRef} />
      </section>

      {error && <div className="error-banner">{error}</div>}

      <form className="chat-input-bar" onSubmit={handleSubmit}>
        <textarea
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={handleTextareaKeyDown}
          placeholder="输入你的问题，Enter 发送，Shift+Enter 换行。"
          rows={2}
          disabled={isSending}
        />
        <button type="submit" disabled={!inputValue.trim() || isSending}>
          {isSending ? '发送中…' : '发送'}
        </button>
      </form>
    </div>
  );
}

async function fetchAssistantReply(messagePayload) {
  // 转义引号和换行符，防止 GraphQL 语法错误
  const userMessage = messagePayload.at(-1).content
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n');

  const response = await fetch(API_BASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `query { response(message: "${userMessage}") }`
    })
  });

  if (!response.ok) {
    throw new Error('网络请求失败');
  }

  const result = await response.json();

  if (result.errors?.length) {
    throw new Error(result.errors[0].message || 'GraphQL 返回错误');
  }

  const assistantPayload = result.data?.response;
  if (!assistantPayload) {
    throw new Error('服务器未返回内容');
  }

  if (Array.isArray(assistantPayload)) {
    const last = assistantPayload[assistantPayload.length - 1];
    if (last?.content) {
      return last.content;
    }
  }

  let content = '';

  if (typeof assistantPayload === 'string') {
    content = assistantPayload;
  } else if (typeof assistantPayload === 'object' && assistantPayload.content) {
    content = assistantPayload.content;
  } else if (Array.isArray(assistantPayload)) {
    const last = assistantPayload[assistantPayload.length - 1];
    content = last?.content || JSON.stringify(assistantPayload);
  } else {
    content = JSON.stringify(assistantPayload);
  }

  // 如果返回的是转义后的字符串（如 "\\n"），将其转换为真正的换行符
  // 检查是否包含转义的换行符
  if (content.includes('\\n')) {
    content = content.replace(/\\n/g, '\n');
  }

  return content;
}

export default App;
